import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';

const SignUp = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    institution: '',
    phone: '',
    department: '',
    password: '',
    plan: '1 Month',
    paymentMethod: 'bkash',
    coupon: ''
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planParam = params.get('plan');
    if (planParam) {
      setFormData(prev => ({ ...prev, plan: planParam }));
    }
  }, [location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Here we would handle the backend submission
    alert('Registration functionality requires backend integration. Please enable Youbase.');
  };

  return (
    <div className="min-h-screen bg-rich-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <GraduationCap className="h-12 w-12 text-vibrant-red" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Join CampusQube VIP
        </h2>
        <p className="mt-2 text-center text-sm text-medium-gray">
          Or{' '}
          <Link to="/signin" className="font-medium text-vibrant-red hover:text-crimson">
            sign in to your account
          </Link>
        </p>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-dark-charcoal py-8 px-4 shadow-xl border border-white/10 sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-light-gray">
                Full Name *
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-white/20 rounded-md shadow-sm placeholder-medium-gray bg-dark-gray text-white focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-light-gray">
                Email address *
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-white/20 rounded-md shadow-sm placeholder-medium-gray bg-dark-gray text-white focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="institution" className="block text-sm font-medium text-light-gray">
                Institution *
              </label>
              <div className="mt-1">
                <input
                  id="institution"
                  name="institution"
                  type="text"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-white/20 rounded-md shadow-sm placeholder-medium-gray bg-dark-gray text-white focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm"
                  value={formData.institution}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="department" className="block text-sm font-medium text-light-gray">
                Department *
              </label>
              <div className="mt-1">
                <input
                  id="department"
                  name="department"
                  type="text"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-white/20 rounded-md shadow-sm placeholder-medium-gray bg-dark-gray text-white focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm"
                  value={formData.department}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-light-gray">Phone Number (optional)</label>
              <div className="mt-1">
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  className="appearance-none block w-full px-3 py-2 border border-white/20 rounded-md shadow-sm placeholder-medium-gray bg-dark-gray text-white focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-light-gray">
                Password *
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-white/20 rounded-md shadow-sm placeholder-medium-gray bg-dark-gray text-white focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <h3 className="text-lg font-medium text-white mb-4">Payment Details</h3>
              
              <div className="mb-4">
                <label htmlFor="plan" className="block text-sm font-medium text-light-gray">
                  Plan *
                </label>
                <select
                  id="plan"
                  name="plan"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-white/20 focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm rounded-md bg-dark-gray text-white"
                  value={formData.plan}
                  onChange={handleChange}
                >
                  <option value="1 Month">1 Month</option>
                  <option value="6 Months">6 Months</option>
                  <option value="1 Year">1 Year</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="paymentMethod" className="block text-sm font-medium text-light-gray">
                  Select Payment Method
                </label>
                <select
                  id="paymentMethod"
                  name="paymentMethod"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-white/20 focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm rounded-md bg-dark-gray text-white"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                >
                  <option value="bkash">bKash</option>
                  <option value="nagad">Nagad</option>
                  <option value="card">Credit/Debit Card</option>
                </select>
              </div>

              <div>
                <label htmlFor="coupon" className="block text-sm font-medium text-light-gray">
                  Coupon Code (Optional)
                </label>
                <div className="mt-1">
                  <input
                    id="coupon"
                    name="coupon"
                    type="text"
                    className="appearance-none block w-full px-3 py-2 border border-white/20 rounded-md shadow-sm placeholder-medium-gray bg-dark-gray text-white focus:outline-none focus:ring-vibrant-red focus:border-vibrant-red sm:text-sm"
                    value={formData.coupon}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-button-gradient hover:shadow-glow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vibrant-red transition-all"
              >
                Complete Registration
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
